/**
 * ACORDE — «Mi ACORDE» (sections/acorde-mi-cuenta.liquid).
 *
 * Lo que vive en el teléfono del cliente se pinta aquí, con la tarjeta real de la colección
 * (`/products/<handle>?view=tarjeta`, la misma que usa la página de favoritos):
 *
 * - Perfil del quiz      → localStorage `acorde_perfil`  (lo guarda acorde-quiz.js)
 * - Favoritos            → localStorage `acorde_favoritos` (acorde-favoritos.js)
 * - Vistos recientemente → localStorage `acorde_vistos` y `acorde_ultimo_visto` (snippets/acorde-base)
 * - Recomendados         → los del quiz; sin quiz, las recomendaciones de Shopify para el último visto.
 *
 * Cada carril se carga cuando se acerca a la pantalla. Nada de esto sale del teléfono.
 *
 * Con sesión, también abre el detalle de cada pedido (hoja propia) y «Volver a comprar».
 */
const CLAVES = {
  perfil: 'acorde_perfil',
  favoritos: 'acorde_favoritos',
  vistos: 'acorde_vistos',
  ultimo: 'acorde_ultimo_visto',
};

const RAIZ = window.Shopify?.routes?.root || '/';

function leer(clave, porDefecto) {
  try {
    const valor = JSON.parse(localStorage.getItem(clave) || 'null');
    return valor ?? porDefecto;
  } catch {
    return porDefecto;
  }
}

function borrar(clave) {
  try {
    localStorage.removeItem(clave);
  } catch {
    // Sin almacenamiento no hay nada que borrar.
  }
}

/** Una sola petición por perfume aunque aparezca en varios carriles. */
const tarjetas = new Map();

function pedirTarjeta(handle) {
  if (!tarjetas.has(handle)) {
    const promesa = fetch(`${RAIZ}products/${encodeURIComponent(handle)}?view=tarjeta`, {
      headers: { Accept: 'text/html' },
    })
      .then((respuesta) => (respuesta.ok ? respuesta.text() : null))
      .then((html) => {
        if (!html) return null;
        const documento = new DOMParser().parseFromString(html, 'text/html');
        const envoltorio = documento.querySelector('.acorde-tarjeta-sola');
        return envoltorio?.querySelector('product-card') ? envoltorio : null;
      })
      .catch(() => null);
    tarjetas.set(handle, promesa);
  }
  return tarjetas.get(handle).then((nodo) => (nodo ? nodo.cloneNode(true) : null));
}

class AcordeMiCuenta extends HTMLElement {
  connectedCallback() {
    this.maximo = parseInt(this.dataset.maximo || '8', 10);

    // Con la entrada propia (D-68) las pestañas se inician cuando la cuenta llega (acorde-cuenta-app.js).
    if (this.dataset.motor !== 'acorde') this.iniciarPestanas();
    this.contarFavoritos();
    this.pintarPerfil();
    this.alAcercarse(this.fila('recomendados'), () => this.cargarRecomendados());
    this.alAcercarse(this.fila('favoritos'), () => this.cargarFavoritos());
    this.alAcercarse(this.fila('vistos'), () => this.cargarVistos());

    // Si el cliente quita un corazón aquí mismo, el carril de favoritos se actualiza.
    this.alCambiarFavoritos = () => {
      this.contarFavoritos();
      this.cargarFavoritos();
    };
    document.addEventListener('acorde:favoritos', this.alCambiarFavoritos);

    this.addEventListener('click', (evento) => {
      if (evento.target.closest('[data-borrar-vistos]')) {
        borrar(CLAVES.vistos);
        borrar(CLAVES.ultimo);
        this.fila('vistos').hidden = true;
      }
      if (evento.target.closest('[data-abrir-nota]')) {
        document.querySelector('acorde-nota-asistente')?.abrir?.();
      }

      // Detalle de un pedido: hoja propia (snippets/acorde-cuenta-pedido, modo «detalle»).
      const abrirPedido = evento.target.closest('[data-abrir-pedido]');
      if (abrirPedido) this.abrirHoja(document.getElementById(abrirPedido.dataset.abrirPedido));

      const hoja = evento.target.closest('dialog.acorde-hoja');
      if (hoja && (evento.target === hoja || evento.target.closest('[data-cerrar-hoja]'))) this.cerrarHoja(hoja);

      const volver = evento.target.closest('[data-volver-a-comprar]');
      if (volver) this.volverAComprar(volver);

      const pestana = evento.target.closest('[data-pestana], [data-ir-pestana]');
      if (pestana) this.irA(pestana.dataset.pestana || pestana.dataset.irPestana, true);

      const filtro = evento.target.closest('[data-filtro]');
      if (filtro) this.filtrarPedidos(filtro);
    });

    // Esc cierra con la misma animación. Se escucha en captura porque `cancel` y `close` no burbujean.
    this.addEventListener(
      'cancel',
      (evento) => {
        if (!evento.target.matches?.('dialog.acorde-hoja')) return;
        evento.preventDefault();
        this.cerrarHoja(evento.target);
      },
      true
    );
    // Si el navegador la cierra por su cuenta (Esc dos veces), la página vuelve a desplazarse igual.
    this.addEventListener(
      'close',
      (evento) => {
        if (!evento.target.matches?.('dialog.acorde-hoja')) return;
        evento.target.classList.remove('acorde-hoja--visible');
        document.documentElement.classList.remove('acorde-hoja-abierta');
      },
      true
    );

    if (!document.querySelector('acorde-nota-asistente')) {
      this.querySelector('[data-abrir-nota]')?.closest('li')?.remove();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('acorde:favoritos', this.alCambiarFavoritos);
    this.observador?.disconnect();
  }

  // ---------------------------------------------------------------- pestañas

  /** Resumen, Pedidos, Para ti, Perfil, Direcciones. La pestaña queda en la dirección (#pedidos). */
  iniciarPestanas() {
    this.pestanas = [...this.querySelectorAll('[data-pestana]')];
    if (!this.pestanas.length) return;
    const inicial = window.location.hash.slice(1);
    this.irA(this.pestanas.some((p) => p.dataset.pestana === inicial) ? inicial : 'resumen', false);
    window.addEventListener('hashchange', () => {
      const nombre = window.location.hash.slice(1);
      if (this.pestanas.some((p) => p.dataset.pestana === nombre)) this.irA(nombre, false);
    });
  }

  irA(nombre, desplazar) {
    if (!this.pestanas?.length || !nombre) return;
    for (const pestana of this.pestanas) {
      const activa = pestana.dataset.pestana === nombre;
      pestana.setAttribute('aria-selected', String(activa));
      pestana.tabIndex = activa ? 0 : -1;
      if (activa) pestana.scrollIntoView({ block: 'nearest', inline: 'center' });
    }
    for (const panel of this.querySelectorAll('[data-panel]')) panel.hidden = panel.dataset.panel !== nombre;
    if (window.location.hash.slice(1) !== nombre) history.replaceState(null, '', `#${nombre}`);
    // Si el cliente ya bajó, se sube hasta el comienzo del panel (justo bajo la barra y las pestañas fijas).
    if (desplazar) {
      const panel = this.querySelector(`[data-panel="${nombre}"]`);
      const fijas = [this.querySelector('.acorde-cuenta__barra-app'), this.querySelector('.acorde-cuenta__pestanas')]
        .filter(Boolean)
        .reduce((total, el) => total + el.offsetHeight, 0);
      const arriba = panel ? panel.getBoundingClientRect().top + window.scrollY - fijas - 8 : 0;
      if (window.scrollY > arriba) window.scrollTo({ top: Math.max(0, arriba), behavior: 'smooth' });
    }
  }

  filtrarPedidos(boton) {
    const grupo = boton.dataset.filtro;
    for (const otro of this.querySelectorAll('[data-filtro]')) otro.setAttribute('aria-pressed', String(otro === boton));
    let visibles = 0;
    for (const tarjeta of this.querySelectorAll('.acorde-pedido__tarjeta[data-estado]')) {
      const estado = tarjeta.dataset.estado;
      const entra =
        grupo === 'todos' ||
        (grupo === 'curso' && (estado === 'preparando' || estado === 'pendiente')) ||
        grupo === estado;
      tarjeta.closest('li').hidden = !entra;
      if (entra) visibles += 1;
    }
    const vacio = this.querySelector('[data-sin-filtrados]');
    if (vacio) vacio.hidden = visibles > 0;
  }

  contarFavoritos() {
    const lista = leer(CLAVES.favoritos, []);
    const cantidad = Array.isArray(lista) ? lista.length : 0;
    for (const conteo of this.querySelectorAll('[data-conteo-favoritos]')) conteo.textContent = cantidad ? String(cantidad) : '';
  }

  // ---------------------------------------------------------------- pedidos

  abrirHoja(hoja) {
    if (!hoja || hoja.open) return;
    hoja.showModal();
    document.documentElement.classList.add('acorde-hoja-abierta');
    requestAnimationFrame(() => hoja.classList.add('acorde-hoja--visible'));
  }

  cerrarHoja(hoja) {
    if (!hoja?.open) return;
    hoja.classList.remove('acorde-hoja--visible');
    document.documentElement.classList.remove('acorde-hoja-abierta');
    setTimeout(() => hoja.close(), 220);
  }

  /** Agrega al carrito los mismos productos del pedido y lleva al carrito. */
  async volverAComprar(boton) {
    let items = [];
    try {
      items = JSON.parse(boton.dataset.volverAComprar || '[]');
    } catch {
      items = [];
    }
    if (!items.length || boton.disabled) return;

    const textoOriginal = boton.textContent;
    boton.disabled = true;
    boton.setAttribute('aria-busy', 'true');

    // Primero todos juntos; si alguno ya no existe o está agotado, uno por uno para salvar el resto.
    let agregados = 0;
    const agregar = (lista) =>
      fetch(`${RAIZ}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lista }),
      }).then((respuesta) => respuesta.ok);

    try {
      if (await agregar(items)) {
        agregados = items.length;
      } else {
        for (const item of items) if (await agregar([item])) agregados += 1;
      }
    } catch {
      agregados = 0;
    }

    if (agregados === items.length) {
      window.location.href = boton.dataset.carrito || `${RAIZ}cart`;
      return;
    }
    boton.textContent = boton.dataset.textoError || textoOriginal;
    boton.removeAttribute('aria-busy');
    if (agregados > 0) {
      setTimeout(() => (window.location.href = boton.dataset.carrito || `${RAIZ}cart`), 1600);
    } else {
      setTimeout(() => {
        boton.textContent = textoOriginal;
        boton.disabled = false;
      }, 2400);
    }
  }

  fila(nombre) {
    return this.querySelector(`[data-fila="${nombre}"]`);
  }

  /** Corre `cargar` una sola vez, cuando la fila está a punto de verse. */
  alAcercarse(fila, cargar) {
    if (!fila) return;
    if (!('IntersectionObserver' in window)) return cargar();
    this.observador ??= new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          if (!entrada.isIntersecting) continue;
          this.observador.unobserve(entrada.target);
          entrada.target._acordeCargar?.();
        }
      },
      { rootMargin: '400px 0px' }
    );
    fila._acordeCargar = cargar;
    // Una fila oculta no se cruza con la pantalla: se mira su lugar en la página a través del padre.
    if (fila.hidden) return cargar();
    this.observador.observe(fila);
  }

  // ---------------------------------------------------------------- perfil del quiz

  pintarPerfil() {
    const caja = this.querySelector('[data-perfil]');
    const invitacion = this.querySelector('[data-sin-quiz]');
    if (!caja || !invitacion) return;

    const perfil = leer(CLAVES.perfil, null);
    const etiquetas = Array.isArray(perfil?.etiquetas) && perfil.etiquetas.length
      ? perfil.etiquetas
      : Object.values(perfil?.respuestas || {}).flat().filter((v) => typeof v === 'string' && v.length > 1);

    if (!etiquetas.length) {
      invitacion.hidden = false;
      return;
    }

    const chips = caja.querySelector('[data-perfil-chips]');
    chips.replaceChildren(
      ...etiquetas.slice(0, 8).map((texto) => {
        const item = document.createElement('li');
        item.textContent = texto.charAt(0).toUpperCase() + texto.slice(1);
        return item;
      })
    );
    caja.hidden = false;
  }

  // ---------------------------------------------------------------- carriles

  async cargarRecomendados() {
    const fila = this.fila('recomendados');
    const perfil = leer(CLAVES.perfil, null);
    let handles = Array.isArray(perfil?.recomendados) ? perfil.recomendados : [];
    let titulo = this.dataset.tituloQuiz;
    let texto = this.dataset.textoQuiz;

    if (!handles.length) {
      const ultimo = leer(CLAVES.ultimo, null);
      if (!ultimo?.id) return;
      try {
        const respuesta = await fetch(
          `${RAIZ}recommendations/products.json?product_id=${encodeURIComponent(ultimo.id)}&limit=${this.maximo}&intent=related`
        );
        const datos = respuesta.ok ? await respuesta.json() : null;
        handles = (datos?.products || []).map((p) => p.handle).filter((h) => h && h !== ultimo.h);
      } catch {
        handles = [];
      }
      titulo = `${this.dataset.tituloVisto} ${ultimo.t || ''}`.trim();
      texto = '';
    }

    if (!handles.length) return;
    fila.querySelector('[data-fila-titulo]').textContent = titulo;
    fila.querySelector('[data-fila-texto]').textContent = texto || '';
    await this.llenar(fila, handles.slice(0, this.maximo));
  }

  async cargarFavoritos() {
    const fila = this.fila('favoritos');
    const lista = leer(CLAVES.favoritos, []);
    const handles = (Array.isArray(lista) ? lista : []).slice().reverse();
    const vacio = fila.querySelector('[data-vacio]');
    const conteo = fila.querySelector('[data-conteo]');
    const verTodo = fila.querySelector('[data-ver-todo]');

    if (conteo) {
      conteo.textContent = `(${handles.length})`;
      conteo.hidden = handles.length === 0;
    }
    if (verTodo) verTodo.hidden = handles.length === 0;

    if (!handles.length) {
      fila.querySelector('[data-carril]').replaceChildren();
      vacio.hidden = false;
      return;
    }
    vacio.hidden = true;
    await this.llenar(fila, handles.slice(0, this.maximo));
  }

  async cargarVistos() {
    const fila = this.fila('vistos');
    const lista = leer(CLAVES.vistos, []);
    const handles = Array.isArray(lista) ? lista : [];
    if (!handles.length) return;
    await this.llenar(fila, handles.slice(0, this.maximo));
  }

  /** Pinta esqueletos, pide las tarjetas y deja solo las que existen. */
  async llenar(fila, handles) {
    const carril = fila.querySelector('[data-carril]');
    fila.hidden = false;
    carril.setAttribute('aria-busy', 'true');
    if (!carril.children.length) {
      carril.replaceChildren(
        ...handles.slice(0, 3).map(() => {
          const item = document.createElement('li');
          item.className = 'acorde-cuenta__esqueleto';
          item.setAttribute('aria-hidden', 'true');
          return item;
        })
      );
    }

    const resultados = await Promise.all(handles.map((handle) => pedirTarjeta(handle)));
    const items = [];
    resultados.forEach((tarjeta, i) => {
      if (!tarjeta) return;
      const item = document.createElement('li');
      item.dataset.handle = handles[i];
      item.appendChild(tarjeta);
      items.push(item);
    });

    carril.replaceChildren(...items);
    carril.setAttribute('aria-busy', 'false');
    if (!items.length && fila.dataset.fila !== 'favoritos') fila.hidden = true;
  }
}

if (!customElements.get('acorde-mi-cuenta')) {
  customElements.define('acorde-mi-cuenta', AcordeMiCuenta);
}
